import { Component, ElementRef } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import {
  AlertController,
  LoadingController,
  ModalController,
  NavController,
  PopoverController,
  ToastController,
} from "ionic-angular";
import SortOptions from "models/content-wrapper/sort-options";
import { FacilityContact } from "models/facilities/facility-details";
import * as moment from "moment";
import { NavigationService } from "navigation/navigation.service";
import { FacilitiesService } from "providers/facilities-service/facilities.service";
import { PatientChartService } from "providers/patient-charts/patient-chart.service";
import { Subscription } from "rxjs";
import { CreateFacilityPage } from "./create-facility/create-facility";
import { ManageCareLevelsPage } from "./manage-care-levels/manage-care-levels";

const FACILITY = "FACILITY";

@Component({
  selector: "facilities-view",
  templateUrl: "facilities-view.html",
})
export class FacilitiesView {
  facilities: any[] = [];
  searchInput: string = "";
  searchBy: string = "team";
  searchMode: boolean = false;
  currentPage: number = 0;
  pages: number[] = [];
  totalPages: number = 0;
  totalElements: number = 0;
  canAddEditFacility: boolean = true;
  sortedBy = "createdDatetime,desc";
  subscriptions: Subscription[] = [];
  sortOptions: SortOptions = new SortOptions("facilityName", "asc");
  pageSize: number = 10;
  gridForm: FormGroup;
  facilitySearchDetails: any;
  data: any = { facilities: [] };

  changesPageSize: number = 10;
  facilityTypeItems: any[];
  statusItems: any[];
  responseMsg: any;
  totalPatients: number = 0;

  constructor(
    private popover: PopoverController,
    private alertCtrl: AlertController,
    private modalCtrl: ModalController,
    private elementRef: ElementRef,
    private toastCtrl: ToastController,
    private fb: FormBuilder,
    private navigationService: NavigationService,
    private loadingCtrl: LoadingController,
    private facilityService: FacilitiesService,
    protected navCtrl: NavController,
    protected patientChartService: PatientChartService
  ) {
    this.gridForm = fb.group({
      facilityType: null,
      status: null,
      searchInput: null,
    });

    this.gridForm.get("facilityType").valueChanges.subscribe((x) => {
      this.currentPage = 0;
      this.getFacilities();
    });

    this.gridForm.get("status").valueChanges.subscribe((x) => {
      this.currentPage = 0;
      this.getFacilities();
    });
  }

  ngOnInit() {
    this.getFacilityTypes();
    this.statusItems = [
      { name: "Active", value: "true" },
      { name: "Archived", value: "false" },
    ];
    this.getFacilities();
  }

  ngAfterViewInit() {
    this.elementRef.nativeElement
      .querySelector(".searchbar-search-icon")
      .addEventListener("click", this.searchFacility.bind(this));
  }

  createSearchRequestBody() {
    let details: any = {};
    details.DataType = FACILITY;
    details.mustFields = [
      {
        name: "dataType",
        value: FACILITY,
      },
    ];
    if (this.gridForm && this.gridForm.controls["facilityType"].value) {
      details.mustFields.push({
        name: "facilityType",
        value: this.gridForm.controls["facilityType"].value,
      });
    }
    if (this.gridForm && this.gridForm.controls["status"].value) {
      details.mustFields.push({
        name: "isActive",
        value:
          this.gridForm.controls["status"].value == "true" ? "true" : "false",
      });
    }
    details.SearchString = this.searchInput ? this.searchInput : null;
    details.dateRange = null;
    details.OptionalFields = [];
    details.sortBy = {
      name: this.sortOptions.column,
      value: this.sortOptions.direction.toLowerCase(),
    };
    details.From = this.currentPage * this.pageSize;
    details.Size = this.pageSize;

    this.facilitySearchDetails = details;
  }

  getFacilityTypes() {
    this.facilityService.getFacilityTypes().subscribe(
      (response) => {
        if (response) {
          const sortArray = (sourceArray) => {
            const sortByLocation = (a, b) =>
              a.qCode.localeCompare(b.qCode, "en", { numeric: true });
            return sourceArray.sort(sortByLocation);
          };
          sortArray(response);
          this.facilityTypeItems = response;
          this.facilityTypeItems.forEach((element) => {
            element.display =
              element["qCode"] + " - " + element["displayValue"];
          });
        }
      },
      (err) => {
        console.log("Error while fetching facility types " + err);
      }
    );
  }

  getFacilities() {
    this.facilities = this.data.facilities;
    this.createSearchRequestBody();
    this.responseMsg = "Loading Facilities...";
    this.subscriptions.push(
      this.facilityService
        .getFacilityFromOpenSearch(this.facilitySearchDetails)
        .subscribe(
          (response) => {
            if (
              response !== null &&
              response["hits"] != null &&
              response["hits"].length > 0
            ) {
              this.formatGridData(response);
            } else {
              this.responseMsg = "No Facility Found";
            }
          },
          (error) => {
            this.responseMsg =
              "Failed to retrieve facilites: Error. " + JSON.stringify(error);
          }
        )
    );
  }

  formatGridData(data) {
    if (data !== null && data["hits"] !== null && data["hits"].length > 0) {
      this.totalPages = Math.ceil(data["totalHits"] / this.pageSize);
      data["hits"].forEach(function (value) {
        value.marEnabled = value.marEnabled ? value.marEnabled : false;
        value.address =
          value.streetAddress.address1 +
          ", " +
          value.streetAddress.city +
          ", " +
          value.streetAddress.state +
          " ";
        if (
          value.streetAddress.zipCode &&
          value.streetAddress.zipCode.length == 9
        ) {
          const zip = value.streetAddress.zipCode.substring(0, 5);
          const zipCodeExt = value.streetAddress.zipCode.substring(5, 9);
          const zipCode = zip + " - " + zipCodeExt;
          value.address += zipCode;
        } else {
          value.address += value.streetAddress.zipCode;
        }

        if (value.streetAddress.county) {
          value.address += ", " + value.streetAddress.county;
        }

        if (
          !value.streetAddress.address1 &&
          !value.streetAddress.city &&
          !value.streetAddress.state &&
          !value.streetAddress.zipCode
        ) {
          value.address = "-";
        }
        value.status = value.isActive == true ? "Active" : "Archived";
        if (
          value.isLegacy &&
          !(value.facilityContacts && value.facilityContacts.length > 0)
        ) {
          const contacts = {} as FacilityContact;
          contacts.mobilePhone = value.phones
            ? value.phones.filter((x) => x.type == "CELL")[0]
              ? value.phones.filter((x) => x.type == "CELL")[0].number
              : "-"
            : "-";
          contacts.officePhone = value.phones
            ? value.phones.filter((x) => x.type == "OFFICE")[0]
              ? value.phones.filter((x) => x.type == "OFFICE")[0].number
              : "-"
            : "-";
          contacts.faxNumber = value.phones
            ? value.phones.filter((x) => x.type == "FAX")[0]
              ? value.phones.filter((x) => x.type == "FAX")[0].number
              : "-"
            : "-";
          value.facilityContacts = [];
          value.facilityContacts.push(contacts);
        }

        if (
          value.isLegacy &&
          !(value.facilityContacts && value.facilityContacts.length > 0)
        ) {
          value.phoneNumber = value.phones
            ? value.phones.filter((x) => x.type == "CELL")[0]
              ? value.phones.filter((x) => x.type == "CELL")[0].number
              : "-"
            : "-";
        } else {
          value.phoneNumber =
            value.phones && value.phones.length > 0
              ? value.phones[0].number
              : "-";
        }

        if (value.phoneNumber && value.phoneNumber != "-") {
          value.phoneNumber = value.phoneNumber
            .replace(/\D+/g, "")
            .replace(/(\d{3})(\d{3})(\d{4})/, "($1)$2-$3");
        }
      });
      this.facilities = data["hits"];
    }
  }

  clearSearch() {
    this.searchInput = null;
    if (this.searchMode) {
      this.currentPage = 0;
      this.searchMode = false;
      this.getFacilities();
    }
  }

  searchFacility() {
    this.searchMode = true;
    this.currentPage = 0;
    this.getFacilities();
  }

  sortBy(selectedCol) {
    if (this.sortOptions.column === selectedCol) {
      this.sortOptions.direction =
        this.sortOptions.direction === "asc" ? "desc" : "asc";
    } else {
      this.sortOptions.column = selectedCol;
      this.sortOptions.direction = "asc";
    }
    this.sortedBy = this.sortOptions.column + "," + this.sortOptions.direction;
    this.getFacilities();
  }

  getFacilitiesByPage(page: number) {
    if (this.currentPage != page) {
      this.currentPage = page;
      this.getFacilities();
    }
  }

  formatPhoneNumber(phone: string) {
    if (!phone) {
      return "";
    }
    let result = phone.replace(/[^0-9,eE\.\+]+/g, "");
    if (result.length > 6) {
      result =
        result.slice(0, 3) + "-" + result.slice(3, 6) + "-" + result.slice(6);
    } else if (result.length > 3) {
      result = result.slice(0, 3) + "-" + result.slice(3);
    }
    return result;
  }

  formatTimezoneDate(stringDate) {
    if (!stringDate) {
      return "";
    }
    return moment.utc(stringDate).local().format("MM/DD/YYYY");
  }

  formatDate(stringDate) {
    if (!stringDate) {
      return "";
    }
    return moment.utc(stringDate).format("MM/DD/YYYY");
  }

  toogleDisplayDetails(facility) {
    facility.displayDetails = !facility.displayDetails;
    if (facility.displayDetails) {
      this.getFacilityCount(facility, false);
    }
    this.addChangesDetails(facility);
  }

  addChangesDetails(facility) {
    facility.currentChangesPage = 0;
    facility.changesPageCount = 0;
    facility.pagedChanges = [];

    if (facility.facilityChanges && facility.facilityChanges.length) {
      facility.facilityChanges.sort(function (a, b) {
        return (
          new Date(b.changeDate).getTime() - new Date(a.changeDate).getTime()
        );
      });
      facility.changesPageCount = Math.ceil(
        facility.facilityChanges.length / this.changesPageSize
      );
      let page = 0;
      facility.pagedChanges[page] = [];
      for (let change of facility.facilityChanges) {
        if (facility.pagedChanges[page].length >= this.pageSize) {
          page++;
          facility.pagedChanges[page] = [];
        }
        if (
          change.fieldName == "MAR Enabled" ||
          change.fieldName == "Billable Room and Board"
        ) {
          change.previousValue = change.previousValue == "true" ? "Yes" : "No";
          change.newValue = change.newValue == "true" ? "Yes" : "No";
        }
        facility.pagedChanges[page].push(change);
      }
    } else {
      facility.currentChangesPage = 0;
      facility.changesPageCount = 0;
      facility.pagedChanges = [];
    }
  }

  changesOnPageChange(event: number, facility) {
    facility.currentChangesPage = event;
  }

  // CO-2315 item #11: <change-history-panel> takes pageChanged as a plain function of the
  // page, because the panel does not know what it is paging. This binds the facility and
  // delegates to changesOnPageChange, which is left EXACTLY as it was so this screen's
  // existing specs keep passing - the condition for extracting the panel at all.
  //
  // The returned closure is cached per facility. Returning a fresh arrow on every change
  // detection cycle would hand the panel a new @Input identity each pass, which in Angular
  // is a re-render on every tick rather than a correctness bug - but on a screen that lists
  // every facility it is the kind of cost nobody goes looking for later.
  changesOnPageChangeFor(facility): (page: number) => void {
    if (!facility.changesPageChangedHandler) {
      facility.changesPageChangedHandler = (page: number) =>
        this.changesOnPageChange(page, facility);
    }
    return facility.changesPageChangedHandler;
  }

  facilityOptions(ev, facility) {
    const options = this.popover.create("FacilitiesViewOptionsPage", facility, {
      cssClass: "facilities-view-options-popover",
    });
    options.onDidDismiss((data) => {
      if (data) {
        if (data === "delete") {
          this.getFacilityCount(facility, true);
        } else if (data === "edit") {
          this.navCtrl.push(CreateFacilityPage, {
            parent: this,
            facilityData: facility,
          });
        } else if (data === "archive") {
          this.openArchiveModal(facility.facilityId, facility.isActive);
        }
      }
    });
    options.present({ ev });
  }

  displayErrorResponse(error) {
    let toast = this.toastCtrl.create({
      message: error.error.displayObject.error,
      duration: 4000,
      position: "below",
      cssClass: "error",
    });
    toast.present();
  }

  newFacility() {
    this.navCtrl.push(CreateFacilityPage, {
      parent: this,
    });
  }

  manageCareLevels() {
    this.navCtrl.push(ManageCareLevelsPage, {
      parent: this,
    });
  }

  onActivate(facility) {
    let data = { facilityId: facility.facilityId, comment: "", isActive: true };
    return this.facilityService
      .archiveFacilityToggle(data)
      .pipe()
      .take(1)
      .subscribe(
        (o) => {
          setTimeout(() => this.getFacilities(), 2000);
        },
        (x) => console.log("Error")
      );
  }

  openArchiveModal(facilityId: number, isActive: boolean) {
    let archiveModal = this.modalCtrl.create(
      "FacilitiesViewArchiveModal",
      {
        facilityId: facilityId,
        isActive: isActive,
      },
      { enableBackdropDismiss: true, cssClass: "facilities-view-archive-modal" }
    );

    archiveModal.onDidDismiss((data) => {
      if (data && data.dismiss) {
        delete data.dismiss;
        data.isActive = false;
        return this.facilityService
          .archiveFacilityToggle(data)
          .pipe()
          .take(1)
          .subscribe(
            (o) => {
              setTimeout(() => this.getFacilities(), 2000);
            },
            (x) => console.log("Error")
          );
      }
    });

    archiveModal.present();
  }

  getFacilityCount(facility, deleteFacility: boolean) {
    let total = 0;
    let facilityId = facility.orderManagementFacilityId
      ? facility.orderManagementFacilityId
      : facility.facilityId;
    this.patientChartService
      .getFacilityCountByPatient(facilityId, deleteFacility)
      .subscribe((charts) => {
        if (charts) {
          let statusCount = charts.statusCounts;
          let referral = statusCount.REFERRAL;
          let onService = statusCount.ON_SERVICE;
          let evaluation = statusCount.EVALUATION;
          let disEnrolled = statusCount.DIS_ENROLLED;
          let discharged = statusCount.DISCHARGED;
          let declinedCare = statusCount.DECLINED_CARE;
          let cancelled = statusCount.CANCELLED;
          if (deleteFacility) {
            total =
              referral +
              onService +
              evaluation +
              discharged +
              disEnrolled +
              declinedCare +
              cancelled;
          } else {
            total = onService;
          }
          this.totalPatients = total;
          facility.numberOfPatients = this.totalPatients;
          if (deleteFacility) {
            this.deleteFacility(total, facility.facilityId);
          }
        }
      });
  }

  openDeleteModal(facilityId: number) {
    let deleteModal = this.modalCtrl.create(
      "FacilitiesViewDeleteModal",
      {
        facilityId: facilityId,
      },
      { enableBackdropDismiss: true, cssClass: "facilities-view-delete-modal" }
    );

    deleteModal.onDidDismiss((data) => {
      if (data) {
        delete data.dismiss;
      }
    });

    deleteModal.present();
  }

  deleteFacility(total, facilityId) {
    if (total > 0) {
      this.openDeleteModal(facilityId);
    } else {
      this.facilityService.deleteFacility(facilityId).subscribe((x) => {
        this.getFacilities();
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((val) => val.unsubscribe());
  }

  getDisplayValue(value) {
    if (this.facilityTypeItems && this.facilityTypeItems.length > 0 && value) {
      value = this.facilityTypeItems.filter((x) => x.enumValue == value)[0]
        ? this.facilityTypeItems.filter((x) => x.enumValue == value)[0]
            .displayValue
        : value;
    }
    return value;
  }

  formatUSPhoneNumber(phoneNumber: any) {
    if (phoneNumber) {
      return phoneNumber
        .replace(/\D+/g, "")
        .replace(/(\d{3})(\d{3})(\d{4})/, "($1)$2-$3");
    }
    return "-";
  }
}



// WEBPACK FOOTER //
// ./src/pages/company/facilities-view/facilities-view.ts